/**
 * Sweep Control — Run Sweep with a confirm step that names the paid APIs, then
 * the honest fire-and-return dispatched state (run id + Refresh health) or the
 * "a sweep is already running" state (run_in_progress — a state, not an error).
 * The parent watches only the returned run id and reports completion from the
 * existing authorized health read; this component never invents progress.
 */

import { useEffect, useState } from "react";
import { useTriggerSweep } from "../hooks";
import { triggerPhase } from "../logic";
import type { SweepDisclosure } from "../types";
import { IconPlay, IconRefresh } from "./icons";

export function RunSweep({
  onRefreshHealth,
  onWatchRun,
  disclosure,
  disclosurePending,
  disclosureError,
}: {
  onRefreshHealth: () => void;
  onWatchRun: (runId: string) => void;
  disclosure: SweepDisclosure | undefined;
  disclosurePending: boolean;
  disclosureError: boolean;
}): JSX.Element {
  const trigger = useTriggerSweep();
  const [confirming, setConfirming] = useState(false);
  const result = trigger.data;

  useEffect(() => {
    if (!result) return;
    const phase = triggerPhase(result);
    const runId =
      phase === "dispatched"
        ? result.sweep_run_id
        : (result.running_sweep_id ?? result.sweep_run_id);
    if (runId) onWatchRun(runId);
  }, [onWatchRun, result]);

  if (result) {
    const phase = triggerPhase(result);
    return (
      <div className="sxsw-dispatch">
        {phase === "dispatched" ? (
          <>
            <p className="sxsw-dispatch-title">Sweep dispatched</p>
            <p className="sxsw-dispatch-copy">
              The sweep was queued and runs outside this interface. Settings is watching the health
              log and will confirm when it finishes.
            </p>
            <p className="sxsw-dispatch-meta">Run {result.sweep_run_id}</p>
          </>
        ) : (
          <>
            <p className="sxsw-dispatch-title">A sweep is already running</p>
            <p className="sxsw-dispatch-copy">
              A sweep is already in progress, so a new one was not started. Settings will watch that
              run instead.
            </p>
            {result.running_sweep_id && (
              <p className="sxsw-dispatch-meta">Running {result.running_sweep_id}</p>
            )}
          </>
        )}
        <div className="sxsw-dispatch-actions">
          <button type="button" className="sx-btn sx-btn-p2" onClick={onRefreshHealth}>
            <IconRefresh />
            Refresh health
          </button>
          <button
            type="button"
            className="sx-btn sx-btn-p3"
            onClick={() => {
              trigger.reset();
              setConfirming(false);
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  if (confirming) {
    if (!disclosure) {
      return (
        <div className="sxsw-confirm">
          <p className="sxsw-error">
            Run disclosure is unavailable. Refresh health before dispatching a sweep.
          </p>
          <button type="button" className="sx-btn sx-btn-p3" onClick={onRefreshHealth}>
            <IconRefresh />
            Refresh health
          </button>
        </div>
      );
    }
    return (
      <div className="sxsw-confirm">
        <p className="sxsw-confirm-title">Run a manual sweep?</p>
        <p className="sxsw-confirm-copy">Enabled intake sources for this run:</p>
        <ul className="sxsw-confirm-apis">
          {disclosure.intakeSources.map((source) => (
            <li key={source}>{source}</li>
          ))}
        </ul>
        {disclosure.enrichmentServices.length > 0 && (
          <>
            <p className="sxsw-confirm-copy">Enabled enrichment services:</p>
            <ul className="sxsw-confirm-apis">
              {disclosure.enrichmentServices.map((service) => (
                <li key={service}>{service}</li>
              ))}
            </ul>
          </>
        )}
        <p className="sxsw-confirm-copy">
          {disclosure.costSources.length > 0
            ? `Cost sources: ${disclosure.costSources.join(", ")}. `
            : "No paid service is enabled. "}
          Allow about {disclosure.expectedDurationMinutes} minutes.
        </p>
        {trigger.isError && (
          <p className="sxsw-error">
            {trigger.error instanceof Error ? trigger.error.message : "Failed to dispatch sweep."}
          </p>
        )}
        <div className="sxsw-confirm-actions">
          <button
            type="button"
            className="sx-btn sx-btn-hero"
            onClick={() => trigger.mutate()}
            disabled={trigger.isPending}
          >
            {trigger.isPending ? "Dispatching…" : "Confirm & run sweep"}
          </button>
          <button
            type="button"
            className="sx-btn sx-btn-p3"
            onClick={() => {
              // Reset the mutation too (mirroring Done): otherwise a failed
              // dispatch's error would persist and reappear on the next confirm.
              trigger.reset();
              setConfirming(false);
            }}
            disabled={trigger.isPending}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sxsw-run-cta">
      <button
        type="button"
        className="sx-btn sx-btn-p2"
        onClick={() => setConfirming(true)}
        disabled={!disclosure}
      >
        <IconPlay />
        Run sweep
      </button>
      <p className="sxsw-hint">
        {disclosurePending
          ? "Loading the enabled services for the confirmation gate."
          : disclosureError || !disclosure
            ? "Run disclosure is unavailable. Refresh health before dispatching."
            : "Manually dispatches a firm-wide signal sweep. Confirmation names enabled intake, enrichment, cost, and duration first."}
      </p>
    </div>
  );
}
